if (!customElements.get('lp-story-viewer')) {
  customElements.define(
    'lp-story-viewer',
    class LpStoryViewer extends HTMLElement {
      constructor() {
        super();
        this.stories = [];
        this.storyIndex = 0;
        this.mediaIndex = 0;
        this.timer = null;
        this.segmentDuration = 4000;
      }

      connectedCallback() {
        const dataScript = this.querySelector('.lp-stories__data');
        if (!dataScript) return;

        try {
          this.stories = JSON.parse(dataScript.textContent).filter(
            (story) => story.images && story.images.length
          );
        } catch (error) {
          this.stories = [];
        }

        this.viewer = this.querySelector('.lp-stories__viewer');
        this.progressEl = this.querySelector('.lp-stories__progress');
        this.mediaEl = this.querySelector('.lp-stories__media');
        this.captionHeadingEl = this.querySelector('.lp-stories__caption-heading');
        this.captionTextEl = this.querySelector('.lp-stories__caption-text');
        this.avatarEl = this.querySelector('.lp-stories__viewer-avatar');
        this.nameEl = this.querySelector('.lp-stories__viewer-name');

        if (!this.viewer) return;

        this.querySelectorAll('.lp-stories__trigger').forEach((trigger, index) => {
          trigger.addEventListener('click', () => this.open(index));
        });

        this.querySelector('.lp-stories__close')?.addEventListener('click', () => this.close());
        this.querySelector('.lp-stories__nav--prev')?.addEventListener('click', () => this.prev());
        this.querySelector('.lp-stories__nav--next')?.addEventListener('click', () => this.next());

        this.viewer.addEventListener('click', (event) => {
          if (event.target === this.viewer) this.close();
        });

        this.boundKeydown = (event) => {
          if (this.viewer.hidden) return;
          if (event.key === 'Escape') this.close();
          if (event.key === 'ArrowLeft') this.prev();
          if (event.key === 'ArrowRight') this.next();
        };
        document.addEventListener('keydown', this.boundKeydown);

        this.mediaEl?.addEventListener('pointerdown', () => this.pause());
        this.mediaEl?.addEventListener('pointerup', () => this.resume());
        this.mediaEl?.addEventListener('pointercancel', () => this.resume());
      }

      disconnectedCallback() {
        if (this.boundKeydown) document.removeEventListener('keydown', this.boundKeydown);
        this.stopTimer();
      }

      open(storyIndex) {
        if (!this.stories.length) return;
        this.storyIndex = Math.max(0, Math.min(storyIndex, this.stories.length - 1));
        this.mediaIndex = 0;
        this.viewer.hidden = false;
        document.body.classList.add('lp-stories-lock-scroll');
        this.render();
      }

      close() {
        this.viewer.hidden = true;
        document.body.classList.remove('lp-stories-lock-scroll');
        this.stopTimer();
      }

      render() {
        const story = this.stories[this.storyIndex];
        if (!story) {
          this.close();
          return;
        }
        const media = story.images[this.mediaIndex];
        if (!media) {
          this.close();
          return;
        }

        if (this.avatarEl) this.avatarEl.src = story.avatar || '';
        if (this.nameEl) this.nameEl.textContent = story.name || '';
        if (this.captionHeadingEl) this.captionHeadingEl.textContent = story.heading || '';
        if (this.captionTextEl) this.captionTextEl.innerHTML = story.text || '';

        if (this.mediaEl) {
          this.mediaEl.innerHTML = '';
          const img = document.createElement('img');
          img.src = media.src;
          img.alt = media.alt || '';
          this.mediaEl.appendChild(img);
        }

        this.buildProgress(story.images.length);
        this.startTimer();
      }

      buildProgress(count) {
        if (!this.progressEl) return;
        this.progressEl.innerHTML = '';
        for (let i = 0; i < count; i += 1) {
          const bar = document.createElement('span');
          bar.className = 'lp-stories__progress-bar';
          if (i < this.mediaIndex) bar.classList.add('is-complete');
          this.progressEl.appendChild(bar);
        }
      }

      startTimer() {
        this.stopTimer();
        const bar = this.progressEl?.children[this.mediaIndex];
        if (bar) {
          bar.style.animationDuration = `${this.segmentDuration}ms`;
          bar.classList.add('is-running');
        }
        this.timer = setTimeout(() => this.next(), this.segmentDuration);
      }

      stopTimer() {
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
      }

      pause() {
        this.stopTimer();
      }

      resume() {
        if (!this.viewer.hidden) this.startTimer();
      }

      next() {
        const story = this.stories[this.storyIndex];
        if (!story) return;
        if (this.mediaIndex < story.images.length - 1) {
          this.mediaIndex += 1;
          this.render();
        } else if (this.storyIndex < this.stories.length - 1) {
          this.storyIndex += 1;
          this.mediaIndex = 0;
          this.render();
        } else {
          this.close();
        }
      }

      prev() {
        if (this.mediaIndex > 0) {
          this.mediaIndex -= 1;
          this.render();
        } else if (this.storyIndex > 0) {
          this.storyIndex -= 1;
          this.mediaIndex = this.stories[this.storyIndex].images.length - 1;
          this.render();
        }
      }
    }
  );
}

if (!customElements.get('lp-feed-carousel')) {
  customElements.define(
    'lp-feed-carousel',
    class LpFeedCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.lp-feed-carousel__track');
        if (!this.track) return;

        this.slides = Array.from(this.track.children);
        if (this.slides.length <= 1) return;

        this.speed = parseInt(this.getAttribute('speed'), 10) || 500;

        // loop="true": 最後→最初へ逆戻りせずに継ぎ目なくループ / loop="false": 最後のスライドで停止
        // loop属性なし: 従来どおり最後から最初へ戻る
        const loopAttr = this.getAttribute('loop');
        this.loop = loopAttr === 'true';
        this.stopAtEnd = loopAttr === 'false';
        this.allSlides = this.slides;

        if (this.loop) {
          // 先頭スライドの複製を末尾に置き、そこへ送った後に本物の先頭へ瞬時に戻す
          this.cloneSlide = this.slides[0].cloneNode(true);
          this.cloneSlide.classList.add('is-clone');
          this.cloneSlide.setAttribute('aria-hidden', 'true');
          this.cloneSlide.removeAttribute('data-shopify-editor-block');
          this.track.appendChild(this.cloneSlide);
          this.allSlides = [...this.slides, this.cloneSlide];
          this.cloneSlide.querySelectorAll('video').forEach((video) => {
            video.muted = true;
            video.play().catch(() => {});
          });
        }

        this.indicatorGroups = [];
        const dotsWrap = this.querySelector('.lp-feed-carousel__dots');
        if (dotsWrap) this.indicatorGroups.push(this.buildIndicators(dotsWrap, 'lp-feed-carousel__dot'));

        const barsWrap = this.querySelector('.lp-feed-carousel__bars');
        if (barsWrap) this.indicatorGroups.push(this.buildIndicators(barsWrap, 'lp-feed-carousel__bar'));

        let ticking = false;
        this.track.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.updateActiveIndex();
            ticking = false;
          });
          if (this.loop) {
            // スクロールが止まった時点で複製スライドにいれば本物の先頭へ戻す
            clearTimeout(this.scrollEndTimer);
            this.scrollEndTimer = setTimeout(() => this.jumpFromClone(), 150);
          }
        });

        this.autoplayDelay = parseInt(this.getAttribute('autoplay'), 10);
        if (this.autoplayDelay > 0) {
          this.startAutoplay();
          this.track.addEventListener('pointerdown', () => this.stopAutoplay());
          this.track.addEventListener('pointerup', () => this.startAutoplay());
          this.track.addEventListener('pointercancel', () => this.startAutoplay());
        }
      }

      disconnectedCallback() {
        this.stopAutoplay();
        clearTimeout(this.scrollEndTimer);
      }

      buildIndicators(wrap, itemClass) {
        wrap.innerHTML = '';
        return this.slides.map((slide, index) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.className = itemClass;
          item.setAttribute('aria-label', `${index + 1}枚目を表示`);
          if (index === 0) item.classList.add('is-active');
          item.addEventListener('click', () => this.goToSlide(index));
          wrap.appendChild(item);
          return item;
        });
      }

      // 複製スライドを含めた位置のインデックス（複製スライド = this.slides.length）
      getClosestIndex() {
        const trackRect = this.track.getBoundingClientRect();
        let closestIndex = 0;
        let closestDistance = Infinity;
        this.allSlides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        return closestIndex;
      }

      jumpFromClone() {
        if (this.animating || this.getClosestIndex() !== this.slides.length) return;
        const trackRect = this.track.getBoundingClientRect();
        this.track.scrollLeft += this.slides[0].getBoundingClientRect().left - trackRect.left;
      }

      goToSlide(index) {
        const slide = this.allSlides[index];
        if (!slide) return;

        const trackRect = this.track.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const startX = this.track.scrollLeft;
        const distance = slideRect.left - trackRect.left;
        if (Math.abs(distance) < 1) return;

        const duration = this.speed;
        const startTime = performance.now();
        this.animating = true;

        const animate = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          const eased = 1 - (1 - progress) ** 3;
          this.track.scrollLeft = startX + distance * eased;
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            this.animating = false;
            if (this.loop) this.jumpFromClone();
          }
        };
        requestAnimationFrame(animate);
      }

      startAutoplay() {
        this.stopAutoplay();
        this.autoplayTimer = setInterval(() => {
          const currentIndex = this.getClosestIndex();
          if (this.loop) {
            // 最後のスライドからは末尾の複製（先頭と同じ見た目）へ送る
            this.goToSlide(currentIndex >= this.slides.length ? 1 : currentIndex + 1);
            return;
          }
          if (this.stopAtEnd && currentIndex >= this.slides.length - 1) {
            this.stopAutoplay();
            return;
          }
          const nextIndex = (currentIndex + 1) % this.slides.length;
          this.goToSlide(nextIndex);
        }, this.autoplayDelay);
      }

      stopAutoplay() {
        if (this.autoplayTimer) clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      updateActiveIndex() {
        const closestIndex = this.getClosestIndex() % this.slides.length;
        this.indicatorGroups.forEach((group) => {
          group.forEach((item, index) => item.classList.toggle('is-active', index === closestIndex));
        });
      }
    }
  );
}

if (!customElements.get('lp-show-more-grid')) {
  customElements.define(
    'lp-show-more-grid',
    class LpShowMoreGrid extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('.lp-instagram__grid');
        this.trigger = this.querySelector('.lp-instagram__view-more');
        const button = this.querySelector('.lp-instagram__view-more-button');
        if (!this.grid || !this.trigger || !button) return;

        button.addEventListener('click', () => {
          this.grid.classList.add('is-expanded');
          this.trigger.hidden = true;
        });
      }
    }
  );
}
